import React, { useEffect, useState } from 'react'
import { Modal, Form, DatePicker } from 'antd'
import styled from 'styled-components'
import moment from 'moment'
import {
  ACTUAL_DELIVERY_DATE_REQUIRED_MESSAGE,
  parseActualDeliveryDate,
  validateActualDeliveryDate,
} from 'src/utils/actualDeliveryDate'

/**
 * Confirm Actual Delivery Date (blank by default — never today).
 * Primary path: dashboard Delivered. Also used by Accept when no date yet
 * (e.g. supplier-confirmed orders that skip the Delivered button).
 */
const ActualDeliveryDateModal = ({
  visible,
  record,
  title = 'Accept as ordered',
  okText = 'Accept as ordered',
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [actualDeliveryDate, setActualDeliveryDate] = useState(null)
  const [dateError, setDateError] = useState('')

  useEffect(() => {
    if (!visible) return
    const existing = parseActualDeliveryDate(record?.actualDeliveryDate)
    setActualDeliveryDate(
      existing.ok ? moment(existing.ymd, 'YYYY-MM-DD') : null,
    )
    setDateError('')
  }, [visible, record])

  if (!record) return null

  const handleOk = () => {
    const check = validateActualDeliveryDate(actualDeliveryDate, {
      required: true,
    })
    if (!check.ok) {
      setDateError(check.message || ACTUAL_DELIVERY_DATE_REQUIRED_MESSAGE)
      return
    }
    onConfirm(check.ymd)
  }

  const handleCancel = () => {
    setActualDeliveryDate(null)
    setDateError('')
    onCancel()
  }

  return (
    <StyledModal
      title={title}
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={okText}
      cancelText='Cancel'
      okButtonProps={{ type: 'primary', loading }}
      confirmLoading={loading}
      destroyOnClose
    >
      <OrderDetails>
        <DetailRow>Supplier: {record.supplier?.title || '—'}</DetailRow>
        <DetailRow>PO Number: {record.purchaseOrderNumber || '—'}</DetailRow>
      </OrderDetails>

      <Form layout='vertical'>
        <Form.Item
          label='Actual Delivery Date'
          required
          validateStatus={dateError ? 'error' : ''}
          help={
            dateError ||
            'Select when the goods physically arrived. This is not filled with today’s date automatically.'
          }
        >
          <StyledDatePicker
            value={actualDeliveryDate}
            onChange={value => {
              setActualDeliveryDate(value)
              if (dateError) setDateError('')
            }}
            format='DD/MM/YYYY'
            placeholder='Select the date goods arrived'
            style={{ width: '100%' }}
            disabledDate={current =>
              current && current.isAfter(moment().endOf('day'))
            }
            allowClear
          />
        </Form.Item>
      </Form>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`

const OrderDetails = styled.div`
  margin-bottom: 14px;
`

const DetailRow = styled.p`
  margin: 4px 0;
  color: #595959;
  font-size: 13px;
`

export default ActualDeliveryDateModal
